import {Button} from '@dropbox/dig-components/buttons';
import {Box} from '@dropbox/dig-foundations';
import {UIIconProps} from '@dropbox/dig-icons';
import {
  AnalyticsLine,
  EditLine,
  GratitudeLine,
  NewsLine,
  PersonMultipleLine,
  WorkstreamsLine,
} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import chatgptConnect from 'assets/images/chatgpt-connect.png';
import chatgptUsage from 'assets/images/chatgpt-usage.png';
import claudeConnect from 'assets/images/claude-connect.png';
import cursorConnect from 'assets/images/cursor-connect.png';
import cursorInstall from 'assets/images/cursor-install.png';
import claudeLogo from 'assets/logos/claude.svg';
import cursorLogoDark from 'assets/logos/cursor_dark.svg';
import cursorLogoLight from 'assets/logos/cursor_light.svg';
import openaiLogoLight from 'assets/logos/openai.svg';
import openaiLogoDark from 'assets/logos/openai_dark.svg';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {t} from 'i18next';
import {ReactNode} from 'react';

export const MCP_SERVER_URL = 'https://mcp.os.dropbox.com';

// ChatGPT plugin/connector install link. Currently a developer-preview URL
// (pending approval). The `plugin_asdk_app_…` id is the stable app identifier,
// so this should survive publishing — but if OpenAI changes the path once it's
// approved, just update it here.
export const CHATGPT_PLUGIN_URL =
  'https://chatgpt.com/plugins/plugin_asdk_app_6a6b63c1c4bc819197358457ccd6d6a5';

// Claude connector directory listing for CompanyOS, used by claude.ai and the
// Claude desktop app. The uuid is the stable connector id.
export const CLAUDE_CONNECTOR_URL =
  'https://claude.ai/directory/connectors/56d4fb7f-bb92-4d80-a4aa-3e0811b79efa';

// One-click Cursor install deeplink (base64-encodes the same transport config
// Cursor would otherwise read from mcp.json).
export const CURSOR_DEEPLINK =
  'cursor://anysphere.cursor-deeplink/mcp/install?name=CompanyOS&config=' +
  btoa(JSON.stringify({url: MCP_SERVER_URL}));

export type Capability = {group: string; description: string; icon: UIIconProps['src']};

type CapabilitySpec = {groupKey: string; descriptionKey: string; icon: UIIconProps['src']};

const CAPABILITY_SPECS: CapabilitySpec[] = [
  {
    groupKey: 'mcp_setup_capability_status_updates_group',
    descriptionKey: 'mcp_setup_capability_status_updates_description',
    icon: EditLine,
  },
  {
    groupKey: 'mcp_setup_capability_workstreams_metrics_group',
    descriptionKey: 'mcp_setup_capability_workstreams_metrics_description',
    icon: WorkstreamsLine,
  },
  {
    groupKey: 'mcp_setup_capability_counts_trends_group',
    descriptionKey: 'mcp_setup_capability_counts_trends_description',
    icon: AnalyticsLine,
  },
  {
    groupKey: 'mcp_setup_capability_people_teams_group',
    descriptionKey: 'mcp_setup_capability_people_teams_description',
    icon: PersonMultipleLine,
  },
  {
    groupKey: 'mcp_setup_capability_news_group',
    descriptionKey: 'mcp_setup_capability_news_description',
    icon: NewsLine,
  },
  {
    groupKey: 'mcp_setup_capability_recognition_group',
    descriptionKey: 'mcp_setup_capability_recognition_description',
    icon: GratitudeLine,
  },
];

export const resolveCapabilities = (): Capability[] =>
  CAPABILITY_SPECS.map(({groupKey, descriptionKey, icon}) => ({
    group: t(groupKey),
    description: t(descriptionKey),
    icon,
  }));

export type SetupStep = {
  title: string;
  body?: ReactNode;
  code?: string;
  action?: ReactNode;
  image?: string;
  imageAlt?: string;
};

// Brand logos come as theme-specific variants (monochrome marks need to flip
// for dark mode); single-color marks just point both at the same file.
export type ThemedLogo = {light: string; dark: string};

export type ClientGuide = {
  id: string;
  name: string;
  logo?: ThemedLogo;
  // Rail icon for clients without a brand logo (e.g. the built-in agent).
  icon?: UIIconProps['src'];
  // Renders the Ask showcase panel instead of copy/paste install steps.
  builtIn?: boolean;
  steps: SetupStep[];
};

const CURSOR_LOGO: ThemedLogo = {light: cursorLogoLight, dark: cursorLogoDark};
const CLAUDE_LOGO: ThemedLogo = {light: claudeLogo, dark: claudeLogo};
const OPENAI_LOGO: ThemedLogo = {light: openaiLogoLight, dark: openaiLogoDark};

export const CLIENT_GUIDES: ClientGuide[] = [
  {
    id: 'CompanyOS',
    name: 'CompanyOS',
    icon: CompanyAskGlyph,
    builtIn: true,
    steps: [],
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    logo: OPENAI_LOGO,
    steps: [
      {
        title: 'Install the CompanyOS plugin',
        body: 'Open the CompanyOS plugin in ChatGPT and click Install plugin.',
        action: (
          <Box>
            <Button
              variant="primary"
              onClick={() => {
                analyticsLogger().logEvent('MCP_SETUP_CTA_CLICKED', {target: 'chatgpt_plugin'});
                window.open(CHATGPT_PLUGIN_URL, '_blank', 'noopener,noreferrer');
              }}
            >
              Install plugin
            </Button>
          </Box>
        ),
      },
      {
        title: 'Sign in with CompanyOS',
        body: 'Approve the connection with Dropbox SSO to link ChatGPT to your account.',
        image: chatgptConnect,
        imageAlt:
          'ChatGPT "Connect CompanyOS" dialog, approved by your admin, with a Continue to CompanyOS button',
      },
      {
        title: 'Ask away',
        body: 'Mention @companyos in any chat and ask about workstreams, people, teams, and news.',
        image: chatgptUsage,
        imageAlt:
          'ChatGPT composer showing the CompanyOS plugin in the Plugins list with @companyos typed in the message box',
      },
    ],
  },
  {
    id: 'codex',
    name: 'Codex CLI',
    logo: OPENAI_LOGO,
    steps: [
      {
        title: 'Add the server',
        code: `codex mcp add CompanyOS --url ${MCP_SERVER_URL}`,
      },
      {
        title: 'Sign in',
        code: 'codex mcp login CompanyOS',
      },
    ],
  },
  {
    id: 'claude',
    name: 'Claude',
    logo: CLAUDE_LOGO,
    steps: [
      {
        title: 'Add the CompanyOS connector',
        body: 'Open the CompanyOS connector in Claude and click Connect.',
        action: (
          <Box>
            <Button
              variant="primary"
              onClick={() => {
                analyticsLogger().logEvent('MCP_SETUP_CTA_CLICKED', {target: 'claude_connector'});
                window.open(CLAUDE_CONNECTOR_URL, '_blank', 'noopener,noreferrer');
              }}
            >
              Add connector
            </Button>
          </Box>
        ),
      },
      {
        title: 'Sign in with CompanyOS',
        body: 'Approve the connection with Dropbox SSO to link Claude to your account.',
        image: claudeConnect,
        imageAlt:
          'Claude connector detail page for CompanyOS at mcp.os.dropbox.com with a Connect button',
      },
    ],
  },
  {
    id: 'claude-code',
    name: 'Claude Code',
    logo: CLAUDE_LOGO,
    steps: [
      {
        title: 'Add the server',
        code: `claude mcp add --transport http CompanyOS ${MCP_SERVER_URL}`,
      },
      {
        title: 'Sign in',
        code: 'claude mcp login CompanyOS',
      },
    ],
  },
  {
    id: 'cursor',
    name: 'Cursor',
    logo: CURSOR_LOGO,
    steps: [
      {
        title: 'Add CompanyOS to Cursor',
        action: (
          <Box>
            <Button
              variant="primary"
              onClick={() => {
                analyticsLogger().logEvent('MCP_SETUP_CTA_CLICKED', {target: 'cursor_deeplink'});
                window.location.href = CURSOR_DEEPLINK;
              }}
            >
              Add to Cursor
            </Button>
          </Box>
        ),
      },
      {
        title: 'Confirm installation in Cursor',
        image: cursorInstall,
        imageAlt: 'Cursor "Install MCP Server?" dialog for the CompanyOS server',
      },
      {
        title: 'Connect with Dropbox SSO',
        image: cursorConnect,
        imageAlt:
          'Cursor CompanyOS server card showing "Needs authentication" and a Connect button',
      },
    ],
  },
  {
    id: 'custom',
    name: 'Custom MCP',
    steps: [
      {
        title: 'Point your client to this MCP endpoint',
        body: 'Add as a Streamable HTTP MCP server using this URL, then authenticate with Dropbox SSO.',
        code: MCP_SERVER_URL,
      },
    ],
  },
];

/** External MCP clients only (excludes the built-in Ask agent). */
export const EXTERNAL_CLIENT_GUIDES: ClientGuide[] = CLIENT_GUIDES.filter((g) => !g.builtIn);
